/**
 * Same-origin baked media paths (public/data) — no backend, no CDN for small assets in the SPA bundle.
 * Indices get coverUrlWebp/coverUrlAvif fields from scripts/studio/bake-gallery-media-index.mjs at build time.
 */

/** S3 key prefix → Amplify static path under public/data */
export const GALLERY_BAKED_PREFIXES = [
  { prefix: 'gallery/studio/theater/', publicBase: '/data/gallery/studio-theater/' },
  { prefix: 'gallery/studio/collection/', publicBase: '/data/gallery/studio-collection/' },
  { prefix: 'gallery/studio/playlists/', publicBase: '/data/gallery/studio-playlists/' },
  { prefix: 'gallery/studio/radio/icons/', publicBase: '/data/gallery/studio/radio/icons/' },
  { prefix: 'gallery/studio/musicians/', publicBase: '/data/gallery/studio/musicians/' },
];

const memoryBakedHit = new Map();

export function normalizeGalleryS3Key(raw) {
  return String(raw || '').replace(/^\/+/, '').trim();
}

/** Map S3 object key → same-origin URL if layout matches a baked catalog prefix. */
export function s3KeyToBakedPublicUrl(s3Key) {
  const key = normalizeGalleryS3Key(s3Key);
  if (!key) return null;
  for (const { prefix, publicBase } of GALLERY_BAKED_PREFIXES) {
    if (key.startsWith(prefix)) {
      return `${publicBase}${key.slice(prefix.length).replace(/\\/g, '/')}`;
    }
  }
  return null;
}

export function isBakedPublicUrl(url) {
  const u = String(url || '').trim();
  return u.startsWith('/data/gallery/');
}

/** Prefer index-baked fields, then key→path mapping. */
export function resolveBakedDisplayUrl(s3Key, entry = null) {
  if (entry) {
    for (const field of ['coverUrlWebp', 'coverUrlAvif', 'coverUrl', 'coverUrlFull', 'thumbUrl', 'url']) {
      const v = entry[field];
      if (typeof v === 'string' && v.trim() && isBakedPublicUrl(v)) return v.trim();
    }
  }
  const key = normalizeGalleryS3Key(s3Key);
  if (!key) return null;
  if (memoryBakedHit.has(key)) return memoryBakedHit.get(key);

  const mapped = s3KeyToBakedPublicUrl(key);
  if (mapped) memoryBakedHit.set(key, mapped);
  return mapped;
}

/**
 * Full-size theater covers (`…/cover.jpg`) are only PARTIALLY baked into
 * public/ (154 of 310 entries), while the `cover-thumb.webp` variant exists for
 * every entry. Mapping a coverKey 1:1 therefore 404s for half the catalog.
 * Rewrite the full cover to the thumb, which is guaranteed to be baked.
 */
function rewriteUnbakedFullCover(url) {
  return String(url || '').replace(/\/cover\.(jpe?g|png)$/i, '/cover-thumb.webp');
}

/** Small static asset: baked path only (images/thumbs). */
export function resolveBakedImageUrl(s3Key, entry = null) {
  if (entry?.coverUrlWebp && isBakedPublicUrl(entry.coverUrlWebp)) return entry.coverUrlWebp;
  if (entry?.coverUrlAvif && isBakedPublicUrl(entry.coverUrlAvif)) return entry.coverUrlAvif;
  const key = normalizeGalleryS3Key(s3Key);
  if (/cover-thumb\.(webp|avif)$/i.test(key)) return s3KeyToBakedPublicUrl(key);
  if (/\.(jpe?g|webp|avif|png|gif)$/i.test(key)) {
    return rewriteUnbakedFullCover(s3KeyToBakedPublicUrl(key));
  }
  return resolveBakedDisplayUrl(s3Key, entry);
}
